/* Governance & setup state: the caller's role on the floor, the member/role
   list, and the Room Factory actions. Roles are enforced by the Conductor —
   this store only mirrors them to shape the UI. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import {
  atriumApi, AtriumApiError,
  type AtriumRole, type FloorMember, type HoodDraft, type Invite, type LayoutRoomGeometry, type RoomDraft, type Team,
} from '@/lib/atriumApi'
import { useFloor } from '@/stores/floor'
import { useToasts } from '@/stores/toasts'

interface AdminState {
  roleLoaded: boolean
  role: AtriumRole | null
  setupAvailable: boolean
  invitesAvailable: boolean
  orgName: string
  members: FloorMember[]
  membersLoaded: boolean
  invites: Invite[]
  invitesLoaded: boolean
  teams: Team[]
  teamsLoaded: boolean
  busy: boolean
}

function errText(e: unknown): string {
  return e instanceof AtriumApiError ? e.message : 'The Atrium backend did not answer.'
}

export const useAdmin = defineStore('admin', {
  state: (): AdminState => ({
    roleLoaded: false,
    role: null,
    setupAvailable: false,
    invitesAvailable: false,
    orgName: '',
    members: [],
    membersLoaded: false,
    invites: [],
    invitesLoaded: false,
    teams: [],
    teamsLoaded: false,
    busy: false,
  }),

  getters: {
    isSteward: (s): boolean => s.role === 'steward' || s.role === 'admin',
    isAdmin: (s): boolean => s.role === 'admin',
  },

  actions: {
    async loadRole(): Promise<void> {
      try {
        const status = await atriumApi.floorStatus()
        this.role = status.role ?? (status.active ? 'member' : null)
        this.setupAvailable = status.setup_available
        this.invitesAvailable = !!status.invites_available
        this.orgName = status.name || ''
      } catch {
        this.role = null
      } finally {
        this.roleLoaded = true
      }
    },

    async loadMembers(): Promise<void> {
      try {
        this.members = (await atriumApi.members()).members
          .filter((m) => !m.is_bot)
          .sort((a, b) => b.power_level - a.power_level || a.user_id.localeCompare(b.user_id))
        this.membersLoaded = true
      } catch (e) {
        useToasts().push({ icon: 'shield', color: 'var(--danger)', title: 'Couldn’t load members', text: errText(e) })
      }
    },

    async setupFloor(token: string, name: string, seedRooms: boolean): Promise<boolean> {
      this.busy = true
      try {
        await atriumApi.setupFloor(token, name, seedRooms)
        await matrix.refreshDiscovery()
        await useFloor().loadFloor()
        await this.loadRole()
        useToasts().push({ icon: 'floor', color: 'var(--gold)', title: 'The floor is open', text: 'Your office exists now.' })
        return true
      } catch (e) {
        useToasts().push({ icon: 'shield', color: 'var(--danger)', title: 'Floor setup failed', text: errText(e) })
        return false
      } finally {
        this.busy = false
      }
    },

    async claimAdmin(token: string): Promise<boolean> {
      this.busy = true
      try {
        await atriumApi.claimAdmin(token)
        await this.loadRole()
        useToasts().push({ icon: 'shield', color: 'var(--gold)', title: 'You are an admin now' })
        return true
      } catch (e) {
        useToasts().push({ icon: 'shield', color: 'var(--danger)', title: 'Claim failed', text: errText(e) })
        return false
      } finally {
        this.busy = false
      }
    },

    async createRoom(draft: RoomDraft): Promise<boolean> {
      this.busy = true
      try {
        const entry = await atriumApi.createRoom(draft)
        await useFloor().refresh()
        useToasts().push({ icon: 'plus', color: 'var(--gold)', title: `“${entry.name}” is on the floor` })
        return true
      } catch (e) {
        useToasts().push({ icon: 'board', color: 'var(--danger)', title: 'Couldn’t create the room', text: errText(e) })
        return false
      } finally {
        this.busy = false
      }
    },

    async updateRoom(slug: string, draft: RoomDraft): Promise<boolean> {
      this.busy = true
      try {
        await atriumApi.updateRoom(slug, draft)
        await useFloor().refresh()
        return true
      } catch (e) {
        useToasts().push({ icon: 'board', color: 'var(--danger)', title: 'Couldn’t update the room', text: errText(e) })
        return false
      } finally {
        this.busy = false
      }
    },

    async archiveRoom(slug: string): Promise<boolean> {
      this.busy = true
      try {
        await atriumApi.archiveRoom(slug)
        await useFloor().refresh()
        useToasts().push({ icon: 'leave', color: 'var(--gold)', title: 'Room archived', text: 'Its history survives in Matrix.' })
        return true
      } catch (e) {
        useToasts().push({ icon: 'board', color: 'var(--danger)', title: 'Couldn’t archive the room', text: errText(e) })
        return false
      } finally {
        this.busy = false
      }
    },

    async setRole(userId: string, role: AtriumRole): Promise<void> {
      this.busy = true
      try {
        await atriumApi.setRole(userId, role)
        await this.loadMembers()
      } catch (e) {
        useToasts().push({ icon: 'shield', color: 'var(--danger)', title: 'Couldn’t change the role', text: errText(e) })
      } finally {
        this.busy = false
      }
    },

    async saveLayout(rooms: LayoutRoomGeometry[], hoods: HoodDraft[]): Promise<boolean> {
      this.busy = true
      try {
        await atriumApi.saveLayout(rooms, hoods)
        await useFloor().refresh()
        useToasts().push({ icon: 'floor', color: 'var(--gold)', title: 'Floor layout saved' })
        return true
      } catch (e) {
        useToasts().push({ icon: 'floor', color: 'var(--danger)', title: 'Couldn’t save the layout', text: errText(e) })
        return false
      } finally {
        this.busy = false
      }
    },

    async renameOrg(name: string): Promise<boolean> {
      try {
        await atriumApi.renameOrg(name)
        this.orgName = name
        useToasts().push({ icon: 'floor', color: 'var(--gold)', title: `The floor is “${name}” now` })
        return true
      } catch (e) {
        useToasts().push({ icon: 'floor', color: 'var(--danger)', title: 'Couldn’t rename', text: errText(e) })
        return false
      }
    },

    async loadInvites(): Promise<void> {
      try {
        this.invites = (await atriumApi.invites()).invites
        this.invitesLoaded = true
      } catch (e) {
        if (e instanceof AtriumApiError && e.status === 503) {
          this.invitesAvailable = false
          this.invitesLoaded = true
          return
        }
        useToasts().push({ icon: 'mail', color: 'var(--danger)', title: 'Couldn’t load invites', text: errText(e) })
      }
    },

    async createInvite(usageLimit: number, expiresHours: number): Promise<Invite | null> {
      this.busy = true
      try {
        const invite = await atriumApi.createInvite(usageLimit, expiresHours)
        this.invites.unshift(invite)
        return invite
      } catch (e) {
        useToasts().push({ icon: 'mail', color: 'var(--danger)', title: 'Couldn’t create the invite', text: errText(e) })
        return null
      } finally {
        this.busy = false
      }
    },

    async loadTeams(): Promise<void> {
      try {
        this.teams = (await atriumApi.teams()).teams
        this.teamsLoaded = true
      } catch (e) {
        useToasts().push({ icon: 'users', color: 'var(--danger)', title: 'Couldn’t load teams', text: errText(e) })
      }
    },

    async createTeam(name: string): Promise<boolean> {
      this.busy = true
      try {
        const team = await atriumApi.createTeam(name)
        this.teams.push(team)
        this.teams.sort((a, b) => a.name.localeCompare(b.name))
        await useFloor().refresh()
        useToasts().push({ icon: 'users', color: 'var(--gold)', title: `Team “${team.name}” exists now`, text: 'Assign people and rooms to it.' })
        return true
      } catch (e) {
        useToasts().push({ icon: 'users', color: 'var(--danger)', title: 'Couldn’t create the team', text: errText(e) })
        return false
      } finally {
        this.busy = false
      }
    },

    async deleteTeam(id: string): Promise<void> {
      this.busy = true
      try {
        const res = await atriumApi.deleteTeam(id)
        this.teams = this.teams.filter((t) => t.id !== id)
        await useFloor().refresh()
        useToasts().push({
          icon: 'users', color: 'var(--gold)', title: 'Team dissolved',
          text: res.rooms_reopened ? `${res.rooms_reopened} room${res.rooms_reopened > 1 ? 's are' : ' is'} open to everyone again.` : undefined,
        })
      } catch (e) {
        useToasts().push({ icon: 'users', color: 'var(--danger)', title: 'Couldn’t dissolve the team', text: errText(e) })
      } finally {
        this.busy = false
      }
    },

    async addTeamMember(id: string, userId: string): Promise<void> {
      this.busy = true
      try {
        await atriumApi.addTeamMember(id, userId)
        await this.loadTeams()
      } catch (e) {
        useToasts().push({ icon: 'users', color: 'var(--danger)', title: 'Couldn’t add to the team', text: errText(e) })
      } finally {
        this.busy = false
      }
    },

    async removeTeamMember(id: string, userId: string): Promise<void> {
      this.busy = true
      try {
        await atriumApi.removeTeamMember(id, userId)
        await this.loadTeams()
      } catch (e) {
        useToasts().push({ icon: 'users', color: 'var(--danger)', title: 'Couldn’t remove from the team', text: errText(e) })
      } finally {
        this.busy = false
      }
    },

    async revokeInvite(id: string): Promise<void> {
      this.busy = true
      try {
        await atriumApi.revokeInvite(id)
        await this.loadInvites()
        useToasts().push({ icon: 'mail', color: 'var(--gold)', title: 'Invite revoked' })
      } catch (e) {
        useToasts().push({ icon: 'mail', color: 'var(--danger)', title: 'Couldn’t revoke the invite', text: errText(e) })
      } finally {
        this.busy = false
      }
    },
  },
})
